import { Link, createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';
import { GuideActionIcon } from '@/components/icons/GuideActionIcon';

export const Route = createFileRoute('/military/')({
  component: MilitaryPage,
});

function MilitaryPage() {
  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'unit-roles', label: 'Unit Roles' },
    { id: 'effectiveness', label: 'Effectiveness & Pay' },
    { id: 'action-links', label: 'Military Actions' },
  ];

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="overview" title="Military: Building Reliable Armies">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <img src="/images/nav/military@3x.png" alt="Military" className="w-full max-w-[120px] rounded-xl border border-[#d4af37]/25" />
          <div className="space-y-3">
            <p>
              Military power is a planning discipline: recruit flow, training queue, wage policy, and return-time
              control. Strong kingdoms win by making their military choices consistent with their war plan.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge>Army timing</Badge>
              <Badge>Role-driven build</Badge>
              <Badge>Queue discipline</Badge>
            </div>
          </div>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="unit-roles" title="Unit Roles and Army Shape">
        <p>
          Soldiers are your pipeline, specialists are role-focused throughput, and elites are your flexible core.
          Your best ratio depends on kingdom role and expected conflict pace.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Soldiers', 'Recruitment buffer and training feedstock.'],
            ['Offense Specialists', 'High immediate pressure for attacking roles.'],
            ['Defense Specialists', 'Stability and anti-chain resilience.'],
            ['Elites', 'Role compression with high versatility.'],
          ].map(([name, note]) => (
            <Card key={name} className="p-4">
              <h3 className="font-cinzel text-lg text-[#f0f0f0]">{name}</h3>
              <p className="text-sm text-[#b0b0b0]">{note}</p>
            </Card>
          ))}
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="effectiveness" title="Effectiveness, Wages, and Readiness">
        <ul className="list-disc space-y-1 pl-5">
          <li>Military pay changes should be planned ahead of war windows, not reacted mid-chain.</li>
          <li>Aggressive drafting increases pressure quickly but can destabilize economy and BE.</li>
          <li>Army quality is not just numbers: return-time alignment and role fit matter more.</li>
          <li>Track readiness as a kingdom-level metric, not only province-by-province.</li>
        </ul>
        <p>
          Related reading: <Link to="/economics" className="text-[#e6c547] underline">Economics</Link>,{' '}
          <Link to="/combat" className="text-[#e6c547] underline">Combat</Link>,{' '}
          <Link to="/wiki/dragons" className="text-[#e6c547] underline">Dragons</Link>.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="action-links" title="Military Actions">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {['traditional_march', 'ambush', 'conquest', 'massacre', 'plunder', 'raze_attack', 'war_spoils', 'capture_prisoners', 'quick_feet'].map(actionId => (
            <Link key={actionId} to="/actions/$actionId" params={{ actionId }}>
              <Card className="p-3 transition-colors hover:border-[#d4af37]/40">
                <div className="flex items-center gap-3">
                  <GuideActionIcon
                    code={actionId}
                    alt={actionId.replace(/_/g, ' ')}
                    width={48}
                    height={48}
                    className="h-12 w-12 overflow-hidden rounded-lg border border-[#d4af37]/25"
                  />
                  <span className="font-semibold text-[#e6c547]">{actionId.replace(/_/g, ' ')}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>
    </div>
  );
}
